/**
 * voxctx REST 클라이언트.
 *
 * 규칙
 * ----
 * 1. 실패는 반드시 예외입니다. `{ error, message }` 를 `ApiError` 로 바꿔 던지고,
 *    호출부가 삼키지 않는 한 화면에 크게 드러납니다 (Loud Failure).
 * 2. 응답을 임의로 보정하지 않습니다. 서버가 주지 않은 값을 만들어 채우지 않습니다.
 * 3. 유료 실행은 `plan()` → `createJob()` → `approveJob()` 순서로만 나갑니다.
 *    이 파일에는 그 순서를 건너뛰는 함수가 없습니다.
 *
 * 개발 중에는 Vite 가 `/api` 를 http://127.0.0.1:33252 로 프록시하므로
 * 상대 경로를 그대로 씁니다 (vite.config.ts 참조).
 */

import type {
  AlignProvidersResponse,
  AlignResponse,
  AnalyzeResponse,
  CompareResponse,
  WaveformResponse,
  AppConfig,
  ApiErrorBody,
  ApproveJobRequest,
  ApproveJobResponse,
  ChunkResponse,
  CreateJobResponse,
  ContextPackRequest,
  ContextPackResponse,
  CostListResponse,
  CostQuery,
  CostSummaryResponse,
  CreateMediaRequest,
  CreateMediaResponse,
  ExportFormat,
  HealthResponse,
  ImportTranscriptRequest,
  ImportTranscriptResponse,
  JobDetailResponse,
  JobEventsResponse,
  JobListResponse,
  JobResponse,
  JobState,
  AudioPreviewState,
  MediaDetailResponse,
  MediaListResponse,
  MergedTranscriptResponse,
  PlanRequest,
  PlanResponse,
  ProviderTestResponse,
  ProvidersResponse,
  SearchParams,
  SearchResponse,
  Speaker,
  SpeakerResponse,
  SpeakersResponse,
  TranscriptDetailResponse,
  WeakRegionsResponse,
  WordsResponse,
  AuthConfig,
  AuthMe,
  SoundMapResponse,
} from '@/types/api'
import type {
  ChatModelsResponse,
  ChatRoom,
  ChatRoomDetailResponse,
  ChatRoomListResponse,
  CreateChatRoomRequest,
  CreateChatRoomResponse,
  DeleteChatRoomResponse,
  SendChatMessageRequest,
  SendChatMessageResponse,
  UpdateChatRoomRequest,
} from '@/types/chat'

export const API_BASE = '/api'

/** 서버가 돌려준 실패를 그대로 담습니다. `code` 는 `docs/API.md` 의 에러 코드입니다. */
export class ApiError extends Error {
  readonly status: number
  readonly code: string

  constructor(status: number, code: string, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }

  /** 예산 초과처럼 사용자가 직접 풀 수 있는 실패인지. */
  get isActionable(): boolean {
    return (
      this.code === 'missing_credential' ||
      this.code === 'budget_exceeded' ||
      this.code === 'plan_changed' ||
      this.code === 'not_approved'
    )
  }
}

type QueryValue = string | number | boolean | null | undefined

function buildQuery(params?: Record<string, QueryValue>): string {
  if (!params) return ''
  const sp = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    sp.append(key, String(value))
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

function isErrorBody(value: unknown): value is ApiErrorBody {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as ApiErrorBody).error === 'string' &&
    typeof (value as ApiErrorBody).message === 'string'
  )
}

interface RequestOptions {
  query?: Record<string, QueryValue>
  body?: unknown
  signal?: AbortSignal
}

async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const url = `${API_BASE}${path}${buildQuery(options.query)}`

  let response: Response
  try {
    response = await fetch(url, {
      method,
      headers:
        options.body === undefined
          ? { Accept: 'application/json' }
          : { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
      // 🔴 세션 쿠키를 반드시 실어 보냅니다. 없으면 vox.instapp.run 에서
      //    로그인해도 모든 요청이 401 로 돌아옵니다.
      credentials: 'same-origin',
    })
  } catch (cause) {
    // 네트워크 자체가 닿지 않은 경우입니다. 서버가 안 떠 있을 때가 대부분입니다.
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new ApiError(
      0,
      'network_error',
      `API 서버에 연결하지 못했습니다 (${method} ${url}). ` +
        `127.0.0.1:33252 에서 voxctx 서버가 실행 중인지 확인하시길 권합니다.`,
    )
  }

  if (response.status === 204) return undefined as T

  const raw = await response.text()
  let parsed: unknown = undefined
  if (raw) {
    try {
      parsed = JSON.parse(raw)
    } catch {
      parsed = undefined
    }
  }

  if (!response.ok) {
    if (isErrorBody(parsed)) {
      throw new ApiError(response.status, parsed.error, parsed.message)
    }
    // JSON 이 아닌 실패(프록시 오류·서버 스택트레이스 등)도 삼키지 않습니다.
    const snippet = raw.slice(0, 300).trim()
    throw new ApiError(
      response.status,
      'http_error',
      `요청이 실패했습니다 (HTTP ${response.status} · ${method} ${url})` +
        (snippet ? `\n${snippet}` : ''),
    )
  }

  if (parsed === undefined) {
    throw new ApiError(
      response.status,
      'invalid_response',
      `응답을 JSON 으로 읽지 못했습니다 (${method} ${url}).`,
    )
  }

  return parsed as T
}

// ─────────────────────────────────────────────────────────────────────────────
// 파일 업로드 — 진행률이 필요해 XMLHttpRequest 를 씁니다 (fetch 는 업로드
// 진행률을 주지 않습니다). 응답 처리 규칙은 위 request() 와 같습니다.
// ─────────────────────────────────────────────────────────────────────────────

export interface UploadOptions {
  /** 0..1 */
  onProgress?: (ratio: number) => void
  signal?: AbortSignal
}

export function uploadMedia(
  file: File,
  options: UploadOptions = {},
): Promise<CreateMediaResponse> {
  return new Promise<CreateMediaResponse>((resolve, reject) => {
    const form = new FormData()
    form.append('file', file) // 🔴 필드명 `file` — docs/API.md 명시

    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${API_BASE}/media/upload`)
    xhr.responseType = 'text'
    // 🔴 업로드도 세션 쿠키가 있어야 통과합니다 (fetch 의 credentials 와 같은 뜻).
    xhr.withCredentials = true

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && options.onProgress) {
        options.onProgress(event.loaded / event.total)
      }
    }

    xhr.onerror = () =>
      reject(
        new ApiError(
          0,
          'network_error',
          '업로드 중 서버 연결이 끊겼습니다. 서버 상태를 확인하시길 권합니다.',
        ),
      )

    xhr.onabort = () => reject(new DOMException('업로드가 취소되었습니다', 'AbortError'))

    xhr.onload = () => {
      let parsed: unknown = undefined
      try {
        parsed = JSON.parse(xhr.responseText)
      } catch {
        parsed = undefined
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        if (parsed === undefined) {
          reject(new ApiError(xhr.status, 'invalid_response', '업로드 응답을 읽지 못했습니다.'))
          return
        }
        resolve(parsed as CreateMediaResponse)
        return
      }
      if (isErrorBody(parsed)) {
        reject(new ApiError(xhr.status, parsed.error, parsed.message))
        return
      }
      reject(
        new ApiError(xhr.status, 'http_error', `업로드가 실패했습니다 (HTTP ${xhr.status})`),
      )
    }

    if (options.signal) {
      options.signal.addEventListener('abort', () => xhr.abort(), { once: true })
    }

    xhr.send(form)
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// 엔드포인트 — docs/API.md 에 적힌 것만 있습니다.
// ─────────────────────────────────────────────────────────────────────────────

export const api = {
  // ── 접근 통제 ─────────────────────────────────────────────────────────
  //
  // 🔴 화면이 막는 것이 아니라 **서버(auth.py)가 막습니다.** 이 호출들은 그
  //    상태를 사람이 이해할 수 있게 보여 주기 위한 것뿐입니다.
  authMe: (signal?: AbortSignal) => request<AuthMe>('GET', '/auth/me', { signal }),
  authConfig: (signal?: AbortSignal) => request<AuthConfig>('GET', '/auth/config', { signal }),
  authGoogle: (credential: string) =>
    request<AuthMe>('POST', '/auth/google', { body: { credential } }),
  authLogout: () => request<{ authenticated: boolean }>('POST', '/auth/logout'),

  /**
   * 🔴 **라벨만 알아도** 화자 이름을 붙입니다.
   *    전역 화자로 통합되지 않은 `speaker_1` 같은 라벨도 이것으로 바꿉니다 —
   *    없으면 서버가 만들어 붙입니다.
   */
  renameSpeakerByKey: (mediaId: number, key: string, displayName: string) =>
    request<{ speaker: Speaker | null }>('PUT', `/media/${mediaId}/speakers/by-key`, {
      body: { key, display_name: displayName },
    }),

  /**
   * 소리 종류 지도 — 말·음악·기계음·잡음·무음.
   * 🔴 추정입니다. 응답의 `estimated` 와 `method` 를 화면에 그대로 보여 줍니다.
   */
  soundMap: (mediaId: number, transcriptId?: number, signal?: AbortSignal) =>
    request<SoundMapResponse>('GET', `/media/${mediaId}/soundmap`, {
      query: transcriptId ? { transcript_id: transcriptId } : undefined,
      signal,
    }),

  // ── 시스템 ────────────────────────────────────────────────────────────
  health: (signal?: AbortSignal) => request<HealthResponse>('GET', '/health', { signal }),

  getConfig: (signal?: AbortSignal) => request<AppConfig>('GET', '/config', { signal }),

  /** 🔴 빈 문자열 키는 서버가 "변경 없음"으로 취급합니다. */
  saveConfig: (config: Partial<AppConfig>) => request<AppConfig>('PUT', '/config', { body: config }),

  // ── 프로바이더 ────────────────────────────────────────────────────────
  listProviders: (signal?: AbortSignal) =>
    request<ProvidersResponse>('GET', '/providers', { signal }),

  /** 키가 없으면 412 `missing_credential` 로 실패합니다. */
  testProvider: (name: string) =>
    request<ProviderTestResponse>('POST', `/providers/${encodeURIComponent(name)}/test`),

  // ── 미디어 ────────────────────────────────────────────────────────────
  createMedia: (body: CreateMediaRequest) =>
    request<CreateMediaResponse>('POST', '/media', { body }),

  uploadMedia,

  listMedia: (
    params: { limit?: number; offset?: number; q?: string } = {},
    signal?: AbortSignal,
  ) => request<MediaListResponse>('GET', '/media', { query: params, signal }),

  getMedia: (id: number, signal?: AbortSignal) =>
    request<MediaDetailResponse>('GET', `/media/${id}`, { signal }),

  deleteMedia: (id: number) => request<{ ok: boolean }>('DELETE', `/media/${id}`),

  /** `<audio>` · wavesurfer 에 그대로 넘길 스트리밍 URL 입니다 (Range 지원). */
  audioUrl: (id: number) => `${API_BASE}/media/${id}/audio`,

  /**
   * 🔴 원본 WAV 스트리밍 URL — 듣기용 압축본이 **아직 없을 때** 씁니다.
   *
   * `MediaDetailResponse.audio_preview.ready` 가 `false` 면 이 주소로 들으십시오.
   * 압축본을 기다리면 12시간 방송은 7분 넘게 무음입니다(2026-10-21 사고).
   * 한 화면에서는 주소를 바꾸지 않습니다 — 재생 도중 같은 주소의 내용이 WAV→M4A 로
   * 바뀌면 브라우저가 이어 받지 못합니다.
   */
  audioRawUrl: (id: number) => `${API_BASE}/media/${id}/audio?raw=1`,

  /** 듣기용 압축본을 백그라운드로 만들기 시작합니다(로컬 ffmpeg, 비용 없음). */
  prepareAudioPreview: (id: number) =>
    request<{ media_id: number; started: 'ready' | 'building' | 'started'; audio_preview: AudioPreviewState | null }>(
      'POST',
      `/media/${id}/audio/preview`,
    ),

  /**
   * 원본 **영상** 스트리밍 URL (Range 지원).
   *
   * 🔴 오디오와 달리 **압축 프리뷰가 아니라 원본**입니다. 9.62시간 영상을
   *    트랜스코딩하면 몇 시간이 걸려 그동안 아무것도 보실 수 없기 때문입니다.
   *    실측한 회차는 `moov` 가 파일 앞에 있어 즉시 스트리밍됩니다.
   * 🔴 `MediaDetailResponse.has_video` 가 `true` 일 때만 부르십시오 —
   *    아니면 404 입니다.
   */
  videoUrl: (id: number) => `${API_BASE}/media/${id}/video`,

  /**
   * 🔴 파형 피크. 오디오 전체를 내려받지 않기 위한 것입니다.
   *
   * 전에는 wavesurfer 에 `url` 만 물려 **파일을 통째로 다운로드**했습니다 —
   * 70.7분 녹음이면 129MB, 391분이면 716MB 를 페이지 열 때마다.
   * 서버가 캐시한 피크는 실측 33KB 입니다(4,056배 작습니다).
   */
  /**
   * 🔴 같은 구간을 전사들이 어떻게 다르게 적었는지 나란히 봅니다.
   * 구간을 창(window)으로 묶어 오므로 25,000구간짜리 전사도 감당됩니다.
   */
  compareTranscripts: (
    mediaId: number,
    opts: { transcripts?: number[]; window_ms?: number; start_ms?: number; end_ms?: number } = {},
    signal?: AbortSignal,
  ) =>
    request<CompareResponse>('GET', `/media/${mediaId}/compare`, {
      query: {
        ...(opts.transcripts?.length ? { transcripts: opts.transcripts.join(',') } : {}),
        ...(opts.window_ms ? { window_ms: opts.window_ms } : {}),
        ...(opts.start_ms != null ? { start_ms: opts.start_ms } : {}),
        ...(opts.end_ms != null ? { end_ms: opts.end_ms } : {}),
      },
      signal,
    }),

  getWaveform: (id: number, buckets?: number, signal?: AbortSignal) =>
    request<WaveformResponse>('GET', `/media/${id}/waveform`, {
      query: buckets ? { buckets } : {},
      signal,
    }),

  // ⚠️ `GET /media/{id}/waveform` 은 문서에는 있으나 **서버에 아직 없습니다**
  //    (2026-08-10 라우트 확인). 없는 엔드포인트를 부르는 함수를 두면 화면이
  //    404 를 "데이터 없음"으로 오해하게 되므로 여기에 만들지 않았습니다.
  //    파형은 `audioUrl()` 을 wavesurfer 에 직접 물리십시오.

  /** 🔴 온디바이스 STT 등 기존 전사문을 1차 참고자료로 들여옵니다. */
  importTranscript: (mediaId: number, body: ImportTranscriptRequest) =>
    request<ImportTranscriptResponse>('POST', `/media/${mediaId}/transcripts/import`, { body }),

  // ── 계획 (dry-run · 무료) ─────────────────────────────────────────────
  /** 저장되지 않고 돈도 들지 않습니다. 예산 초과면 402 `budget_exceeded`. */
  plan: (body: PlanRequest, signal?: AbortSignal) =>
    request<PlanResponse>('POST', '/plan', { body, signal }),

  // ── 작업 ──────────────────────────────────────────────────────────────
  /** 상태 `planned` 로 만듭니다. 아직 아무것도 실행되지 않습니다 (HTTP 201). */
  createJob: (body: PlanRequest) => request<CreateJobResponse>('POST', '/jobs', { body }),

  /** 🔴 여기서만 돈이 나갑니다. 금액이 계획과 다르면 409 `plan_changed`. */
  approveJob: (id: number, body: ApproveJobRequest) =>
    request<ApproveJobResponse>('POST', `/jobs/${id}/approve`, { body }),

  cancelJob: (id: number) => request<JobResponse>('POST', `/jobs/${id}/cancel`),

  listJobs: (params: { state?: JobState; limit?: number } = {}, signal?: AbortSignal) =>
    request<JobListResponse>('GET', '/jobs', { query: params, signal }),

  getJob: (id: number, signal?: AbortSignal) =>
    request<JobDetailResponse>('GET', `/jobs/${id}`, { signal }),

  /** 재접속 직후 놓친 이벤트를 따라잡을 때만 씁니다. 폴링하지 않습니다. */
  getJobEvents: (id: number, afterId = 0, signal?: AbortSignal) =>
    request<JobEventsResponse>('GET', `/jobs/${id}/events`, {
      query: { after_id: afterId },
      signal,
    }),

  // ── 전사 ──────────────────────────────────────────────────────────────
  getTranscript: (id: number, signal?: AbortSignal) =>
    request<TranscriptDetailResponse>('GET', `/transcripts/${id}`, { signal }),

  getWords: (
    id: number,
    range: { start_ms?: number; end_ms?: number } = {},
    signal?: AbortSignal,
  ) => request<WordsResponse>('GET', `/transcripts/${id}/words`, { query: range, signal }),

  getWeakRegions: (id: number, signal?: AbortSignal) =>
    request<WeakRegionsResponse>('GET', `/transcripts/${id}/weak`, { signal }),

  /** 약한 구간 판별 (무료·즉시). 판별 근거(임계값·창)가 함께 옵니다. */
  analyzeTranscript: (id: number) =>
    request<AnalyzeResponse>('POST', `/transcripts/${id}/analyze`),

  /** 청킹 + 색인 (무료). 응답의 `chunks` 는 개수, `sample` 은 앞 3개뿐입니다. */
  chunkTranscript: (id: number) => request<ChunkResponse>('POST', `/transcripts/${id}/chunk`),

  /**
   * 🔴 정렬에 쓸 수 있는 프로바이더와 비용. 쓸 수 없는 것도 **이유와 함께** 옵니다.
   * `seconds` 를 주면 그 길이 기준으로 계산됩니다 (기본 1시간).
   */
  listAligners: (seconds?: number, signal?: AbortSignal) =>
    request<AlignProvidersResponse>('GET', '/align/providers', {
      query: seconds ? { seconds } : {},
      signal,
    }),

  /**
   * 🔴 시간값 없는 전사에 시각을 복원하는 작업을 만듭니다.
   *
   * **아직 실행되지 않습니다.** 정렬은 재전사를 동반해 돈이 들므로 다른 유료
   * 작업과 똑같이 `planned` 로만 만들어집니다 — `approveJob()` 으로 금액을
   * 확인해야 실행됩니다.
   */
  alignTranscript: (id: number, provider: string, force = false) =>
    request<AlignResponse>('POST', `/transcripts/${id}/align`, {
      body: { provider, ...(force ? { force: true } : {}) },
    }),

  /** 다운로드 링크로 그대로 쓰십시오 (JSON 파싱하지 않습니다). */
  exportUrl: (id: number, format: ExportFormat) =>
    `${API_BASE}/transcripts/${id}/export?format=${format}`,

  /** 🔴 여러 tier 를 합친 최선본. 각 구간의 출처가 붙어 옵니다. */
  getMerged: (id: number, signal?: AbortSignal) =>
    request<MergedTranscriptResponse>('GET', `/transcripts/${id}/merged`, { signal }),

  // ── 화자 ──────────────────────────────────────────────────────────────
  listSpeakers: (mediaId: number, signal?: AbortSignal) =>
    request<SpeakersResponse>('GET', `/media/${mediaId}/speakers`, { signal }),

  renameSpeaker: (speakerId: number, displayName: string) =>
    request<SpeakerResponse>('PUT', `/speakers/${speakerId}`, {
      body: { display_name: displayName },
    }),

  // ⚠️ `POST /media/{id}/speakers/reconcile` 도 **서버에 아직 없습니다**.

  // ── 라이브러리 · 컨텍스트 ─────────────────────────────────────────────
  search: (params: SearchParams, signal?: AbortSignal) =>
    request<SearchResponse>('GET', '/library/search', { query: params, signal }),

  /** 🔴 각 조각에 출처(media·시각·tier·transcript_id)가 붙어 옵니다. */
  packContext: (body: ContextPackRequest) =>
    request<ContextPackResponse>('POST', '/context/pack', { body }),

  // ── 비용 ──────────────────────────────────────────────────────────────
  listCosts: (params: CostQuery = {}, signal?: AbortSignal) =>
    request<CostListResponse>('GET', '/costs', { query: params, signal }),

  costSummary: (signal?: AbortSignal) =>
    request<CostSummaryResponse>('GET', '/costs/summary', { signal }),

  // ── 채팅 (전사문 기반) ────────────────────────────────────────────────
  //
  // 🔴 이 엔드포인트들은 **다른 담당자가 만들고 있는 백엔드**입니다. 아직
  //    배포되지 않았다면 404(혹은 501)로 돌아옵니다. 그 실패를 화면에서
  //    "대화가 없습니다" 로 바꿔 그리지 않습니다 — 미배포는 빈 상태가 아니라
  //    **서버가 없는 상태**이며, 그 둘을 섞으면 원인을 찾을 수 없게 됩니다.
  //    (`isBackendMissing()` 참조)
  /**
   * `media_id` 를 주시면 **그 음원에 딸린 대화만** 옵니다.
   * 분석 화면은 한 음원을 보는 자리이므로 늘 이 형태로 부릅니다.
   */
  listChatRooms: (
    params: { media_id?: number; limit?: number } = {},
    signal?: AbortSignal,
  ) => request<ChatRoomListResponse>('GET', '/chat/rooms', { query: params, signal }),

  createChatRoom: (body: CreateChatRoomRequest) =>
    request<CreateChatRoomResponse>('POST', '/chat/rooms', { body }),

  getChatRoom: (roomId: number, signal?: AbortSignal) =>
    request<ChatRoomDetailResponse>('GET', `/chat/rooms/${roomId}`, { signal }),

  /** 이름·기본 모델 변경. `model: null` 은 서버 기본값으로 되돌립니다. */
  updateChatRoom: (roomId: number, body: UpdateChatRoomRequest) =>
    request<ChatRoom>('PATCH', `/chat/rooms/${roomId}`, { body }),

  /**
   * 고를 수 있는 모델과 3단 프리셋(절약 · 기본 · 정밀).
   *
   * 🔴 하드코딩 목록을 쓰지 않는 이유 — 자매 프로젝트에서 셀렉터 여덟 곳이
   *    낡은 채로 남아 404 를 냈습니다. 목록은 늘 서버(→ ai_proxy)에서 옵니다.
   */
  chatModels: (signal?: AbortSignal) =>
    request<ChatModelsResponse>('GET', '/chat/models', { signal }),

  /**
   * 🔴 사용자 메시지와 응답이 **한 번의 왕복으로 함께** 확정됩니다.
   *    스트리밍이 아니므로 응답이 올 때까지 기다립니다 — 그동안 화면은
   *    "보내는 중" 을 사실대로 표시합니다.
   */
  sendChatMessage: (roomId: number, body: SendChatMessageRequest) =>
    request<SendChatMessageResponse>('POST', `/chat/rooms/${roomId}/messages`, { body }),

  deleteChatRoom: (roomId: number) =>
    request<DeleteChatRoomResponse>('DELETE', `/chat/rooms/${roomId}`),
}

/**
 * 🔴 "백엔드가 아직 없다" 와 "데이터가 없다" 를 가릅니다.
 *
 * 라우트가 없으면 501(자리만 잡아 둔 구현)·405(메서드 불일치)·404 로 돌아옵니다.
 * 셋 다 **서버가 준비되지 않았다**는 뜻이며 사용자가 무엇을 잘못한 것이 아닙니다.
 *
 * 🔴 그런데 이 서버에서는 404 가 오지 않습니다 — 2026-08-11 실측:
 *
 *     GET  /api/chat/rooms        → 200 text/html  (SPA index.html)
 *     GET  /api/definitely-not-here → 200 text/html
 *     POST /api/chat/rooms        → 405 text/html
 *
 * 등록되지 않은 경로를 **SPA 폴백 라우트가 먼저 삼켜** 프런트엔드 HTML 을 200 으로
 * 돌려주기 때문입니다. 그래서 `request()` 는 JSON 파싱에 실패해 `invalid_response`
 * 로 던집니다. 이 코드도 "라우트 미등록" 신호로 함께 봅니다 — 그렇게 하지 않으면
 * 화면이 "응답을 읽지 못했습니다" 라는 원인 불명 오류만 보여 주게 됩니다.
 *
 * (백엔드가 `/api/*` 를 폴백에서 제외하면 이 항목은 404 로 정리됩니다. 그때도
 *  아래 판별은 그대로 맞습니다.)
 */
export function isBackendMissing(cause: unknown): boolean {
  if (!(cause instanceof ApiError)) return false
  if (cause.status === 404 || cause.status === 501 || cause.status === 405) return true
  return cause.code === 'invalid_response'
}

export type Api = typeof api
